import { useCallback } from "react";
import useFetch from "../../../../hooks/useFetch";

// ─────────────────────────────────────────────────────────────────────────────
// GUEST USER DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to fetch upcoming and past demo sessions for the guest student.
 * GET /student/demos
 * Returns: list of demo sessions (upcoming + past) with session metadata.
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchDemos = (options = {}) => {
  const { enabled = true, ...restOptions } = options;
  return useFetch("/student/demos", {
    method: "get",
    enabled,
    apiSource: "default",
    ...restOptions,
  });
};

/**
 * Hook to fetch guest student profile and subscription stats.
 * GET /student/guest-dashboard/stats
 * Returns: {
 *   student: {
 *     studentId: string,
 *     studentName: string,
 *     imageUrl: string | null,
 *     timeZone: string,
 *     grade: number,
 *     subscription: {
 *       paymentRequired: boolean,
 *       upgradeNow: boolean,
 *       expiresAt: string | null,
 *     }
 *   }
 * }
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchGuestDashboardStats = (options = {}) => {
  const { enabled = true, ...restOptions } = options;
  return useFetch("/student/guest-dashboard/stats", {
    method: "get",
    enabled,
    apiSource: "default",
    ...restOptions,
  });
};

/**
 * Hook to rate a completed demo session.
 * Manual hook — call mutate({ data: { mentorSlotId, flag } }) to fire.
 * POST /student/demo/rating
 * flag: "good" | "bad"
 * @param {Object} options
 * @returns {Object} - { loading, error, mutate }
 */
export const useRateDemoSession = (options = {}) => {
  const { manual = true, ...restOptions } = options;
  return useFetch("/student/demo/rating", {
    method: "post",
    manual,
    apiSource: "default",
    ...restOptions,
  });
};

/**
 * Hook to reschedule an upcoming demo session.
 * Manual hook — call mutate({ data: { studentId, prevSlotId, curSlotId, timeZone } }) to fire.
 * POST /student/demo/reschedule
 */
export const useRescheduleDemo = (options = {}) => {
  const { manual = true, ...restOptions } = options;
  return useFetch("/student/demo/reschedule", {
    method: "post",
    manual,
    apiSource: "default",
    ...restOptions,
  });
};

/**
 * Hook to cancel an upcoming demo session.
 * Manual hook — call mutate({ data: { studentId, id } }) to fire.
 * DELETE /student/demo/cancel
 */
export const useCancelDemo = (options = {}) => {
  const { manual = true, ...restOptions } = options;
  return useFetch("/student/demo/cancel", {
    method: "post",
    manual,
    apiSource: "default",
    ...restOptions,
  });
};

/**
 * Hook to fetch unpaid order details for the guest student.
 * GET /subscription/unpaid-order
 * Returns: { orderId, masterOrderId, classCredits, modeOfPayment, currency, courses, unpaidPayment, orderCreatedAt }
 * @param {Object} options - Options for the fetch (onSuccess, onError, enabled, etc.)
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useFetchUnpaidOrder = (options = {}) => {
  const { enabled = true, ...restOptions } = options;
  return useFetch("/subscription/unpaid-order", {
    method: "get",
    enabled,
    apiSource: "studentPortal",
    ...restOptions,
  });
};

/**
 * Hook to check the current student's paid status.
 * Manual hook — call mutate() to fire. Lightweight endpoint used for polling
 * after the user proceeds to checkout.
 * GET /student/is-paid
 * Returns: { isPaid: boolean }
 * @param {Object} options
 * @returns {Object} - { apiData, loading, error, refetch, mutate }
 */
export const useCheckIsPaid = (options = {}) => {
  const { manual = true, ...restOptions } = options;
  return useFetch("/student/is-paid", {
    method: "get",
    manual,
    apiSource: "default",
    ...restOptions,
  });
};

/**
 * Hook to fetch eligible demo slots for a given course + timezone.
 * GET /slots
 * Call fetchSlots({ courseId, timeZone }) — startDate is derived from timeZone.
 * @returns {{ allSlots: object, slotsLoading: boolean, fetchSlots: Function }}
 */
export const useSlots = (options = {}) => {
  const { apiData, loading, mutate } = useFetch("/slots", {
    method: "get",
    manual: true,
    apiSource: "default",
    ...options,
  });

  const fetchSlots = useCallback(
    ({ courseId, timeZone }) => {
      if (!courseId || !timeZone) {
        return;
      }
      const startDate = new Date(
        new Date().toLocaleString("en-US", { timeZone }),
      );
      const params = {
        CourseId: courseId,
        timeZone,
        startDate: startDate.toISOString(),
      };
      return mutate({ params })
        .then((data) => data)
        .catch((err) => {
          console.error("[useSlots] /slots error", err);
          throw err;
        });
    },
    [mutate],
  );

  return {
    allSlots: apiData?.eligibleSlots || {},
    slotsLoading: loading,
    fetchSlots,
  };
};
